import React, { lazy, memo, Suspense, useMemo } from 'react'
import { CardContentProps } from './CardContent'

const LazyActionButtons = lazy(() =>
    import('./ActionButtons').then((module) => ({
        default: module.ActionButtons,
    }))
)
const LazyUserAvatarAndDetails = lazy(() =>
    import('./UserAvatarAndDetails').then((module) => ({
        default: module.UserAvatarAndDetails,
    }))
)

/**
 * Header subComponent for the track change card.
 * Renders user avatar, details, and action buttons if applicable.
 * @param {CardContentProps} props - The component props.
 * @returns {JSX.Element} The rendered header for the track change card.
 */
export const TrackChangeCardHeader = memo(
    ({ trackChanges, cardSuggestion, classes, cardType }: CardContentProps) => {
        const shouldShowActionButtons = useMemo(() => {
            if (!trackChanges) return false

            // List all cardType flags that should suppress action buttons
            const hideFlags = [
                cardType.isApprovedAdded,
                cardType.isRejectedAdded,
                cardType.isApprovedDeleted,
                cardType.isRejectedDeleted,
                cardType.isApprovedOptionGroup,
                cardType.isRejectedOptionGroup,
                cardType.isOptionGroup,
                cardType.isModifiedApproved,
                cardType.isModifiedRejected,
                cardType.isReplacedApproved,
                cardType.isReplacedRejected,
            ]

            // If any suppression flag is true, we hide the buttons
            return !hideFlags.some(Boolean)
        }, [trackChanges, cardType])
        const userImageUrl = useMemo(
            () => trackChanges?.userProfileInfo?.imageUrl,
            [trackChanges?.userProfileInfo?.imageUrl]
        )

        return (
            <Suspense fallback={<div className={classes?.placeholderHeader} />}>
                <LazyUserAvatarAndDetails
                    imageUrl={userImageUrl}
                    trackChanges={trackChanges}
                    cardSuggestion={cardSuggestion}
                    classes={classes}
                    shouldShowActionButtons={shouldShowActionButtons}
                >
                    {shouldShowActionButtons && (
                        <LazyActionButtons
                            cardSuggestion={cardSuggestion}
                            trackChanges={trackChanges}
                            classes={classes}
                        />
                    )}
                </LazyUserAvatarAndDetails>
            </Suspense>
        )
    }
)

TrackChangeCardHeader.displayName = 'TrackChangeCardHeader'
